import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  VStack,
  HStack,
  Icon,
  Text,
  Box,
  Image,
  SimpleGrid,
  Input,
  Textarea,
  Select,
  Button,
  FormLabel,
  FormControl,
  Badge,
  useToast,
  Spinner,
  Skeleton,
  IconButton,
} from "@chakra-ui/react";
import { IoStorefrontOutline, IoCheckmarkCircle, IoCloudUploadOutline, IoClose } from "react-icons/io5";
import { useJsApiLoader, Autocomplete, GoogleMap, MarkerF } from "@react-google-maps/api";
import { doc, getDoc, setDoc, deleteDoc, serverTimestamp } from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { db } from "../../firebase/firebase-config";
import { submitVenueOnboarding, updateVenue } from "../../FBFunctions/venueFunctions";
// Shared loader config — must be the same reference everywhere we load
// Google Maps so @react-google-maps/api dedupes the <script> tag.
import {
  GOOGLE_LIBRARIES,
  GOOGLE_MAPS_API_KEY,
  GOOGLE_MAPS_LOADER_ID,
} from "../../lib/googleMaps";

const MAP_CONTAINER_STYLE = { width: "100%", height: "160px", borderRadius: "12px" };
const MAP_OPTIONS = { streetViewControl: false, fullscreenControl: false, mapTypeControl: false };

// ─── Section / field definitions ─────────────────────────────────────────────
// Swap out sections / fields here once you have the final question list.
const SECTIONS = [
  {
    id: "details",
    title: "Venue Details",
    fields: [
      { id: "venueName", label: "Venue Name", type: "text", placeholder: "e.g. The Grand Hall", required: true },
      {
        id: "venueType",
        label: "Venue Type",
        type: "select",
        required: true,
        options: ["Bar / Lounge", "Nightclub", "Restaurant", "Event Space", "Rooftop", "Private Estate", "Other"],
      },
      { id: "description", label: "Description", type: "textarea", placeholder: "Tell us about your venue…", required: true },
    ],
  },
  {
    id: "location",
    title: "Location",
    fields: [
      { id: "_addressSearch", label: "Search Address", type: "address-search", placeholder: "Start typing your venue address…", required: false },
      { id: "streetAddress", label: "Street Address", type: "text", placeholder: "123 Main St", required: true },
      { id: "city", label: "City", type: "text", placeholder: "Vancouver", required: true },
      { id: "province", label: "Province / State", type: "text", placeholder: "BC", required: true },
      { id: "postalCode", label: "Postal / ZIP Code", type: "text", placeholder: "V6B 1A1", required: true },
    ],
  },
  {
    id: "capacity",
    title: "Capacity & Operations",
    fields: [
      { id: "maxCapacity", label: "Max Capacity", type: "number", placeholder: "e.g. 200", required: true },
      { id: "squareFootage", label: "Square Footage (sq ft)", type: "number", placeholder: "e.g. 3500", required: true },
      {
        id: "primaryEventType",
        label: "Primary Event Type",
        type: "select",
        required: true,
        options: ["Concerts / Live Music", "Club Nights", "Private Parties", "Corporate Events", "Weddings", "Pop-ups", "Mixed"],
      },
    ],
  },
  {
    id: "pricing",
    title: "Pricing",
    fields: [
      { id: "minPricePerHour", label: "Minimum Price per Hour (CAD)", type: "number", placeholder: "e.g. 150", required: true },
    ],
  },
  {
    id: "contact",
    title: "Contact Info",
    fields: [
      { id: "contactEmail", label: "Contact Email", type: "email", placeholder: "venue@example.com", required: true },
      { id: "contactPhone", label: "Contact Phone", type: "tel", placeholder: "+1 604 555 0000", required: false },
      { id: "website", label: "Website", type: "url", placeholder: "https://yourvenue.com", required: false },
    ],
  },
  {
    id: "photos",
    title: "Photos",
    fields: [
      { id: "images", label: "Venue Photos", type: "images", required: true },
    ],
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const requiredFieldsFilled = (section, formData) =>
  section.fields
    .filter((f) => f.required)
    .every((f) => {
      const val = formData[f.id];
      if (f.type === "images") return Array.isArray(val) && val.length > 0;
      return String(val ?? "").trim().length > 0;
    });

const saveDraftToFirestore = async (userId, formData, completedSections) => {
  try {
    await setDoc(
      doc(db, "draftVenue", userId),
      { formData, completedSections, updatedAt: serverTimestamp() },
      { merge: true }
    );
  } catch (e) {
    console.error("Draft save failed:", e);
  }
};

// ─── Component ────────────────────────────────────────────────────────────────
export const VenueOnboardingModal = ({ isOpen, onClose, user, venue = null }) => {
  const toast = useToast();
  const isEditMode = !!venue;

  const [formData, setFormData] = useState({});
  const [completedSections, setCompletedSections] = useState(SECTIONS.map(() => false));
  const [openIndex, setOpenIndex] = useState(0);
  const [mapCenter, setMapCenter] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [draftLoading, setDraftLoading] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);

  const autocompleteRef = useRef(null);
  const fileInputRef = useRef(null);

  const { isLoaded: mapsLoaded } = useJsApiLoader({
    id: GOOGLE_MAPS_LOADER_ID,
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_LIBRARIES,
  });

  // ── Load data on open (draft for new, venue for edit) ─────────────────────
  useEffect(() => {
    if (!isOpen || !user) return;

    if (isEditMode) {
      // Prefill form from the existing venue document
      const data = {
        ...venue,
        streetAddress: venue.streetAddress ?? venue.location?.streetAddress,
        city: venue.city ?? venue.location?.city,
        province: venue.province ?? venue.location?.province,
        postalCode: venue.postalCode ?? venue.location?.postalCode,
        lat: venue.coordinates?.lat,
        lng: venue.coordinates?.lng,
        images: venue.images || [],
      };
      setFormData(data);
      setCompletedSections(SECTIONS.map(() => true));
      setOpenIndex(0);
      if (data.lat && data.lng) setMapCenter({ lat: data.lat, lng: data.lng });
      return;
    }

    setDraftLoading(true);
    getDoc(doc(db, "draftVenue", user.uid))
      .then((snap) => {
        if (snap.exists()) {
          const { formData: savedData, completedSections: savedSections } = snap.data();
          setFormData(savedData || {});
          const restored = SECTIONS.map((_, i) => savedSections?.[i] ?? false);
          setCompletedSections(restored);
          const firstIncomplete = restored.findIndex((c) => !c);
          setOpenIndex(firstIncomplete === -1 ? -1 : firstIncomplete);
          if (savedData?.lat && savedData?.lng) {
            setMapCenter({ lat: savedData.lat, lng: savedData.lng });
          }
        }
      })
      .catch(console.error)
      .finally(() => setDraftLoading(false));
  }, [isOpen, user, isEditMode, venue]);

  // ── Auto-save draft (debounced 800 ms, skip in edit mode) ─────────────────
  useEffect(() => {
    if (!isDirty || !user || isEditMode) return;
    const timer = setTimeout(() => {
      saveDraftToFirestore(user.uid, formData, completedSections);
      setIsDirty(false);
    }, 800);
    return () => clearTimeout(timer);
  }, [formData, completedSections, isDirty, user, isEditMode]);

  // ── Field change ───────────────────────────────────────────────────────────
  const handleChange = useCallback((fieldId, value) => {
    setIsDirty(true);
    setFormData((prev) => ({ ...prev, [fieldId]: value }));
  }, []);

  // ── Google Places selection ─────────────────────────────────────────────────
  const handlePlaceChanged = () => {
    const place = autocompleteRef.current?.getPlace();
    if (!place?.geometry) return;

    const get = (type) =>
      place.address_components?.find((c) => c.types.includes(type))?.long_name ?? "";
    const getShort = (type) =>
      place.address_components?.find((c) => c.types.includes(type))?.short_name ?? "";

    const streetNumber = get("street_number");
    const route = get("route");
    const streetAddress = [streetNumber, route].filter(Boolean).join(" ");
    const city = get("locality") || get("sublocality_level_1") || get("postal_town");
    const province = getShort("administrative_area_level_1");
    const postalCode = get("postal_code");
    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();

    setIsDirty(true);
    setFormData((prev) => ({ ...prev, streetAddress, city, province, postalCode, lat, lng }));
    setMapCenter({ lat, lng });
  };

  // ── Image upload / remove ─────────────────────────────────────────────────
  const handleImageUpload = async (files) => {
    if (!files || files.length === 0) return;
    setUploadingImages(true);
    try {
      const storage = getStorage();
      const uploads = await Promise.all(
        Array.from(files).map(async (file) => {
          const path = `venues/${user.uid}/${Date.now()}_${file.name}`;
          const sref = ref(storage, path);
          await uploadBytes(sref, file);
          const url = await getDownloadURL(sref);
          return { url, path };
        })
      );
      setIsDirty(true);
      setFormData((prev) => ({
        ...prev,
        images: [...(prev.images || []), ...uploads],
      }));
    } catch (err) {
      toast({
        title: "Image upload failed",
        description: err.message,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setUploadingImages(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleRemoveImage = async (imageIdx) => {
    const img = formData.images?.[imageIdx];
    setFormData((prev) => ({
      ...prev,
      images: (prev.images || []).filter((_, i) => i !== imageIdx),
    }));
    setIsDirty(true);

    // Best-effort cleanup from Storage
    if (img?.path) {
      try {
        await deleteObject(ref(getStorage(), img.path));
      } catch (err) {
        console.warn("Could not delete image from storage:", err);
      }
    }
  };

  // ── Continue (complete section) ─────────────────────────────────────────────
  const handleContinue = (sIdx) => {
    const section = SECTIONS[sIdx];
    if (!requiredFieldsFilled(section, formData)) {
      toast({
        title: "Fill in all required fields",
        description: `Please complete all required fields in "${section.title}" before continuing.`,
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    const updated = completedSections.map((v, i) => (i === sIdx ? true : v));
    setCompletedSections(updated);
    setIsDirty(false); // cancel any pending debounce before direct save
    // -1 closes all accordion items when everything is done
    const next = updated.findIndex((c) => !c);
    setOpenIndex(next === -1 ? -1 : next);
    // Only persist as a draft for brand-new venues. Edits shouldn't pollute
    // the user's draftVenue doc (that would "revive" an existing venue as a
    // new draft the next time the modal opens for a fresh venue).
    if (!isEditMode) {
      saveDraftToFirestore(user.uid, formData, updated);
    }
  };

  // ── Submit ──────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    setIsDirty(false);
    setSubmitting(true);
    try {
      if (isEditMode) {
        await updateVenue(venue.id, { ...formData, userId: user.uid, userEmail: user.email });
        toast({ title: "Venue updated", status: "success", duration: 3000, isClosable: true });
        handleClose();
        return;
      }
      await submitVenueOnboarding({ ...formData, userId: user.uid, userEmail: user.email });
      await deleteDoc(doc(db, "draftVenue", user.uid));
      setSubmitted(true);
      toast({ title: "Venue submitted!", description: "We'll review and be in touch soon.", status: "success", duration: 4000, isClosable: true });
    } catch (err) {
      toast({ title: isEditMode ? "Update failed" : "Submission failed", description: err.message, status: "error", duration: 5000, isClosable: true });
    } finally {
      setSubmitting(false);
    }
  };

  // ── Close / reset ───────────────────────────────────────────────────────────
  const handleClose = () => {
    setFormData({});
    setCompletedSections(SECTIONS.map(() => false));
    setOpenIndex(0);
    setMapCenter(null);
    setSubmitted(false);
    setIsDirty(false);
    onClose();
  };

  const allComplete = completedSections.every(Boolean);

  // ── Field renderer ──────────────────────────────────────────────────────────
  const renderField = (field, sIdx) => {
    if (field.type === "images") {
      const images = formData.images || [];
      return (
        <FormControl key={field.id} isRequired={field.required}>
          <FormLabel fontSize="xs" fontWeight="semibold" color="gray.600" mb={2}>
            {field.label}
            <Text as="span" fontSize="10px" color="gray.400" ml={2}>
              {images.length > 0 ? `${images.length} uploaded` : "at least 1 required"}
            </Text>
          </FormLabel>

          <SimpleGrid columns={3} spacing={2} mb={3}>
            {images.map((img, i) => (
              <Box
                key={img.url}
                position="relative"
                borderRadius="lg"
                overflow="hidden"
                border="1px solid"
                borderColor="gray.200"
                aspectRatio={1}
              >
                <Image src={img.url} alt={`Venue ${i + 1}`} w="full" h="full" objectFit="cover" />
                <IconButton
                  icon={<IoClose />}
                  aria-label="Remove"
                  size="xs"
                  position="absolute"
                  top={1}
                  right={1}
                  bg="blackAlpha.700"
                  color="white"
                  borderRadius="full"
                  _hover={{ bg: "red.500" }}
                  onClick={() => handleRemoveImage(i)}
                />
              </Box>
            ))}

            {/* Upload button tile */}
            <Box
              as="button"
              type="button"
              onClick={() => fileInputRef.current?.click()}
              aspectRatio={1}
              borderRadius="lg"
              border="1.5px dashed"
              borderColor={uploadingImages ? "#508cf7" : "gray.300"}
              bg={uploadingImages ? "blue.50" : "gray.50"}
              _hover={{ bg: "blue.50", borderColor: "#508cf7" }}
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              transition="all 0.15s"
              disabled={uploadingImages}
            >
              {uploadingImages ? (
                <Spinner size="sm" color="#508cf7" />
              ) : (
                <>
                  <Icon as={IoCloudUploadOutline} boxSize={5} color="#508cf7" />
                  <Text fontSize="10px" color="gray.500" mt={1}>Upload</Text>
                </>
              )}
            </Box>
          </SimpleGrid>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            style={{ display: "none" }}
            onChange={(e) => handleImageUpload(e.target.files)}
          />
        </FormControl>
      );
    }

    if (field.type === "address-search") {
      return (
        <FormControl key={field.id}>
          <FormLabel fontSize="xs" fontWeight="semibold" color="gray.500" mb={1}>
            {field.label}
            <Text as="span" fontSize="10px" color="gray.400" ml={2}>auto-fills fields below</Text>
          </FormLabel>
          {mapsLoaded ? (
            <>
              <Autocomplete
                onLoad={(ref) => { autocompleteRef.current = ref; }}
                onPlaceChanged={handlePlaceChanged}
                options={{ types: ["establishment", "geocode"] }}
              >
                <Input
                  size="sm"
                  borderRadius="lg"
                  placeholder={field.placeholder}
                  focusBorderColor="#508cf7"
                  bg="blue.50"
                />
              </Autocomplete>
              {mapCenter && (
                <Box mt={3} borderRadius="xl" overflow="hidden" border="1px solid" borderColor="gray.200">
                  <GoogleMap
                    mapContainerStyle={MAP_CONTAINER_STYLE}
                    center={mapCenter}
                    zoom={15}
                    options={MAP_OPTIONS}
                  >
                    <MarkerF position={mapCenter} />
                  </GoogleMap>
                </Box>
              )}
            </>
          ) : (
            <Skeleton height="32px" borderRadius="lg" />
          )}
        </FormControl>
      );
    }

    return (
      <FormControl key={field.id} isRequired={field.required}>
        <FormLabel fontSize="xs" fontWeight="semibold" color="gray.600" mb={1}>
          {field.label}
        </FormLabel>
        {field.type === "textarea" ? (
          <Textarea
            size="sm"
            borderRadius="lg"
            placeholder={field.placeholder}
            value={formData[field.id] ?? ""}
            onChange={(e) => handleChange(field.id, e.target.value)}
            focusBorderColor="#508cf7"
            rows={3}
          />
        ) : field.type === "select" ? (
          <Select
            size="sm"
            borderRadius="lg"
            placeholder={`Select ${field.label.toLowerCase()}`}
            value={formData[field.id] ?? ""}
            onChange={(e) => handleChange(field.id, e.target.value)}
            focusBorderColor="#508cf7"
          >
            {field.options.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </Select>
        ) : (
          <Input
            size="sm"
            borderRadius="lg"
            type={field.type}
            placeholder={field.placeholder}
            value={formData[field.id] ?? ""}
            onChange={(e) => handleChange(field.id, e.target.value)}
            focusBorderColor="#508cf7"
          />
        )}
      </FormControl>
    );
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      isCentered
      size={{ base: "full", md: "lg" }}
      scrollBehavior="inside"
    >
      <ModalOverlay backdropFilter="blur(4px)" bg="blackAlpha.500" />
      <ModalContent borderRadius={{ base: 0, md: "2xl" }} overflow="hidden">
        <ModalHeader bg="#508cf7" color="white" fontWeight="bold" fontSize="lg" pb={4} pt={5}>
          <HStack spacing={3}>
            <Icon as={IoStorefrontOutline} boxSize={5} />
            <Text>{isEditMode ? "Edit Venue" : "New Venue"}</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton color="white" top={4} />

        <ModalBody py={6} px={5}>
          {draftLoading ? (
            <VStack spacing={3}>
              {SECTIONS.map((_, i) => <Skeleton key={i} height="52px" borderRadius="xl" />)}
            </VStack>
          ) : submitted ? (
            <VStack spacing={4} textAlign="center" py={8}>
              <Icon as={IoCheckmarkCircle} boxSize={14} color="#508cf7" />
              <Text fontWeight="bold" fontSize="lg" color="gray.800">You're all set!</Text>
              <Text fontSize="sm" color="gray.500" maxW="280px">
                Your venue application has been submitted. We'll review it and reach out shortly.
              </Text>
              <Button bg="#508cf7" color="white" _hover={{ bg: "#226cf1" }} onClick={handleClose} mt={2}>
                Done
              </Button>
            </VStack>
          ) : (
            <VStack spacing={4} align="stretch">
              <HStack justify="space-between">
                <Text fontSize="sm" color="gray.500">{isEditMode ? "Edit any section and save." : "Complete each section to submit."}</Text>
                {isDirty && (
                  <HStack spacing={1}>
                    <Spinner size="xs" color="gray.400" />
                    <Text fontSize="10px" color="gray.400">Saving…</Text>
                  </HStack>
                )}
                {!isDirty && Object.keys(formData).length > 0 && (
                  <Text fontSize="10px" color="gray.400">Draft saved</Text>
                )}
              </HStack>

              <Accordion allowToggle index={openIndex} onChange={(i) => setOpenIndex(i)}>
                {SECTIONS.map((section, sIdx) => {
                  const done = completedSections[sIdx];
                  const canContinue = requiredFieldsFilled(section, formData);
                  const isOpen = openIndex === sIdx;

                  return (
                    <AccordionItem
                      key={section.id}
                      border="1px solid"
                      borderColor={done ? "green.200" : isOpen ? "#508cf7" : "gray.200"}
                      borderRadius="xl"
                      mb={3}
                      overflow="hidden"
                    >
                      <AccordionButton
                        py={3}
                        px={4}
                        bg={done ? "green.50" : "white"}
                        _hover={{ bg: done ? "green.50" : "gray.50" }}
                        _expanded={{ bg: "white", borderBottom: "1px solid", borderColor: "gray.100" }}
                      >
                        <HStack flex="1" spacing={3}>
                          {done ? (
                            <Icon as={IoCheckmarkCircle} color="green.500" boxSize={5} flexShrink={0} />
                          ) : (
                            <Box
                              w="20px" h="20px"
                              borderRadius="full"
                              border="2px solid"
                              borderColor={isOpen ? "#508cf7" : "gray.300"}
                              display="flex" alignItems="center" justifyContent="center"
                              flexShrink={0}
                            >
                              <Text fontSize="10px" fontWeight="bold" color={isOpen ? "#508cf7" : "gray.400"}>
                                {sIdx + 1}
                              </Text>
                            </Box>
                          )}
                          <Text fontWeight="semibold" fontSize="sm" color={done ? "green.700" : "gray.800"}>
                            {section.title}
                          </Text>
                          {done && (
                            <Badge colorScheme="green" fontSize="9px" borderRadius="full" px={2}>
                              Complete
                            </Badge>
                          )}
                        </HStack>
                      </AccordionButton>

                      <AccordionPanel pb={4} px={4} pt={4} bg="white">
                        <VStack spacing={4} align="stretch">
                          {section.fields.map((field) => renderField(field, sIdx))}

                          <Button
                            size="sm"
                            bg={canContinue ? "#508cf7" : "gray.100"}
                            color={canContinue ? "white" : "gray.400"}
                            _hover={{ bg: canContinue ? "#226cf1" : "gray.100" }}
                            borderRadius="lg"
                            alignSelf="flex-end"
                            onClick={() => handleContinue(sIdx)}
                            cursor={canContinue ? "pointer" : "not-allowed"}
                            mt={1}
                          >
                            {done ? "Update & Continue" : "Continue"}
                          </Button>
                        </VStack>
                      </AccordionPanel>
                    </AccordionItem>
                  );
                })}
              </Accordion>

              <Button
                bg="#508cf7"
                color="white"
                _hover={{ bg: "#226cf1" }}
                isDisabled={!allComplete || submitting}
                onClick={handleSubmit}
                borderRadius="xl"
                size="md"
                w="full"
                mt={1}
              >
                {submitting ? <Spinner size="sm" /> : isEditMode ? "Save Changes" : "Submit Venue"}
              </Button>
            </VStack>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
